import React, { Component } from 'react';
import { graphql, createPaginationContainer } from 'react-relay';
import { Link, NavLink } from 'react-router-dom';
import moment from 'moment';
import { isEqual } from 'lodash';
import update from 'immutability-helper';

import { reorderHappenings } from '../../mutations';
import { StoreProvider, PaginatedList } from '../../components';

import HappeningList from './HappeningList';

const pageSize = 100;

export class HappeningsContainerView extends Component {
  constructor(props) {
    super(props);

    const happeningsProps =
      props.lists.heroOffers.edges.map((e) => e.node) || {};

    this.state = {
      happenings: happeningsProps,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.setState({
        happenings: this.props.lists.heroOffers.edges.map((e) => e.node),
      });
    }
  }

  handleLoadMore = (completion) => {
    this.props.relay.loadMore(pageSize, completion);
  };

  handleClick = () => {
    const { relay } = this.props;
    const ids = this.state.happenings.map((happening) => happening.id);
    reorderHappenings(relay.environment, ids, {
      onCompleted: () => {
        window.alert('Successfully reordered happenings');
        this.props.relay.refetchConnection();
      },
      onError: () => {
        window.alert('Failed to reorder happenings');
      },
    });
  };

  moveRow = (dragIndex, hoverIndex) => {
    const { happenings } = this.state;
    const dragHappening = happenings[dragIndex];

    this.setState(
      update(this.state, {
        happenings: {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragHappening],
          ],
        },
      })
    );
  };

  render() {
    const { heroOffers: connection } = this.props.lists;
    const { happenings } = this.state;
    const happeningsOrderUnchanged = isEqual(
      happenings,
      connection.edges.map((e) => e.node)
    );
    const isActive = this.props.match.params.type === 'active';

    return (
      <div className="happenings">
        <div className="header">
          <h2>
            Happenings <span className="amount">({happenings.length})</span>
          </h2>
          <div className="actions">
            <Link to="/happenings/new" className="button">
              New Happening
            </Link>
            {isActive && (
              <button
                disabled={happeningsOrderUnchanged}
                onClick={this.handleClick}
                className="button"
              >
                Update Order
              </button>
            )}
          </div>
        </div>
        <PaginatedList
          canLoadMore={connection.pageInfo.hasNextPage}
          onLoadMore={this.handleLoadMore}
        >
          <div className="tabs">
            <NavLink to="/happenings/tab/active" activeClassName="active">
              Active
            </NavLink>
            <NavLink to="/happenings/tab/upcoming" activeClassName="active">
              Upcoming
            </NavLink>
            <NavLink to="/happenings/tab/expired" activeClassName="active">
              Expired
            </NavLink>
          </div>
          <HappeningList happenings={happenings} moveRow={this.moveRow} />
        </PaginatedList>
      </div>
    );
  }
}

const HappeningsContainerQuery = graphql`
  query HappeningsContainerQuery(
    $first: Int = 100
    $after: String
    $startingAfter: DateTime
    $endingBefore: DateTime
    $active: Boolean = true
  ) {
    lists {
      ...HappeningsContainer_lists
        @arguments(
          first: $first
          after: $after
          startingAfter: $startingAfter
          endingBefore: $endingBefore
          active: $active
        )
    }
  }
`;

const HappeningsContainer = createPaginationContainer(
  HappeningsContainerView,
  {
    lists: graphql`
      fragment HappeningsContainer_lists on Lists
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        startingAfter: { type: "DateTime" }
        endingBefore: { type: "DateTime" }
        active: { type: "Boolean" }
      ) {
        heroOffers(
          first: $first
          after: $after
          startingAfter: $startingAfter
          endingBefore: $endingBefore
          active: $active
        )
          @connection(
            key: "HappeningsContainer_heroOffers"
            filters: ["active", "startingAfter", "endingBefore"]
          ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ...HappeningCell_happening
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps: (props) => props.lists.heroOffers,
    getFragmentVariables: (previousVariables, totalCount) => {
      return {
        ...previousVariables,
        first: totalCount,
      };
    },
    getVariables: (props, paginationInfo, fragmentVariables) => {
      const variables = preprocessParams(props.match.params);

      return {
        ...variables,
        after: paginationInfo.cursor,
        first: pageSize,
      };
    },
    query: HappeningsContainerQuery,
  }
);

const preprocessParams = (params) => {
  const variables = {
    startingAfter: null,
    endingBefore: null,
    active: false,
  };

  switch (params.type) {
    case 'upcoming':
      variables.startingAfter = moment().startOf('hour').toISOString();
      break;
    case 'expired':
      variables.endingBefore = moment().startOf('hour').toISOString();
      break;
    default:
      variables.active = true;
  }

  return variables;
};

export default class HappeningsList extends Component {
  render() {
    return (
      <StoreProvider
        modern
        container={HappeningsContainer}
        preprocessParams={preprocessParams}
        query={HappeningsContainerQuery}
      />
    );
  }
}
